// Piezas compartidas de los Dashboards (Reportes): mismo lenguaje visual que el Inicio (layout/Home/Inicio.jsx).
// Solo presentacion: los dashboards siguen calculando sus datos igual y aqui solo se pintan.
import React, { useState, useEffect, useRef } from "react";
import { Button, Spinner } from "reactstrap";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import * as am5percent from "@amcharts/amcharts5/percent";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import * as am5plugins_exporting from "@amcharts/amcharts5/plugins/exporting";
import am5locales_es_ES from "@amcharts/amcharts5/locales/es_ES";
import "./dashboard.css";

// Dias, meses y menus en español, pero numeros con el formato de México (1,234.56)
export const LOCALE_MX = { ...am5locales_es_ES, _decimalSeparator: ".", _thousandSeparator: "," };

// Mismos colores que el Inicio
export const PALETTE = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#0ea5e9", "#ec4899", "#14b8a6", "#f97316", "#6366f1"];

const GRIS = "#64748b";
const LINEA = "#e2e8f0";

const num = (n) => {
  const v = parseFloat(n);
  return isNaN(v) ? 0 : v;
};

export const fmtMoneda = (n, dec = 2) => {
  const v = num(n);
  return (v < 0 ? "-$" : "$") + Math.abs(v).toLocaleString("es-MX", { minimumFractionDigits: dec, maximumFractionDigits: dec });
};
export const fmtNumero = (n, dec = 2) =>
  num(n).toLocaleString("es-MX", { minimumFractionDigits: dec, maximumFractionDigits: dec });

const FORMATOS = {
  moneda: (n) => fmtMoneda(n),
  numero: (n) => fmtNumero(n),
  entero: (n) => fmtNumero(Math.round(n), 0),
};

// ── Número animado (de su valor anterior al nuevo) ──────────────
function AnimatedNumber({ value, formato }) {
  const destino = num(value);
  const [display, setDisplay] = useState(0);
  const actual = useRef(0);
  const raf = useRef(null);
  useEffect(() => {
    const desde = actual.current;
    const inicio = Date.now();
    const tick = () => {
      const p = Math.min((Date.now() - inicio) / 800, 1);
      const v = p >= 1 ? destino : desde + (destino - desde) * (1 - Math.pow(1 - p, 3));
      actual.current = v;
      setDisplay(v);
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [destino]);
  return <span>{formato(display)}</span>;
}

// ── Anillo de progreso con icono al centro ──────────────────────
function Ring({ pct = 100, color, icon, size = 42, stroke = 5 }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(isFinite(pct) ? pct : 0, 100));
  return (
    <div className="dash-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={LINEA} strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={`${(circ * p) / 100} ${circ}`}
        />
      </svg>
      {icon && <i className={icon} style={{ color }} />}
    </div>
  );
}

// ── Página: encabezado (icono, título, subtítulo, acciones, Regresar) ──
export function DashPage({ icon, color = "#3b82f6", bg = "#eff6ff", title, subtitle, cargando, actions, back = "/Dashboards", children }) {
  return (
    <div className="dash-page">
      <div className="dash-header">
        <div className="dash-header-title">
          <div className="dash-header-icon" style={{ background: bg, color, borderColor: `${color}40` }}>
            <i className={icon} />
          </div>
          <div style={{ minWidth: 0 }}>
            <h4>{title}</h4>
            <p>
              {subtitle}
              {cargando && (
                <span className="dash-loading">
                  <i className="fas fa-circle-notch fa-spin" style={{ marginRight: 5 }} />
                  Cargando datos...
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="dash-header-actions">
          {actions}
          {back && (
            <Button size="sm" color="danger" href={back}>
              <i className="fas fa-arrow-left" />
              Regresar
            </Button>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

// ── Barra de filtros ────────────────────────────────────────────
export function FilterBar({ children }) {
  return <div className="dash-filters">{children}</div>;
}

export function Filtro({ label, chico, children }) {
  return (
    <div className={chico ? "dash-filter dash-filter-sm" : "dash-filter"}>
      <label>{label}</label>
      {children}
    </div>
  );
}

export function FiltroSeparador() {
  return <div className="dash-filter-sep" />;
}

export function BotonActualizar({ onClick, cargando }) {
  return (
    <div className="dash-filter-btn">
      <Button size="sm" color="primary" onClick={onClick} disabled={cargando}>
        <i className={cargando ? "fas fa-sync-alt fa-spin" : "fas fa-sync-alt"} />
        Actualizar
      </Button>
    </div>
  );
}

// ── KPIs ────────────────────────────────────────────────────────
// item: { label, value, formato: "moneda" | "numero" | "entero", icon, color, pct, sub }
export function KpiCard({ label, value, formato = "numero", icon, color = "#3b82f6", pct, sub }) {
  const f = FORMATOS[formato] || FORMATOS.numero;
  return (
    <div className="dash-kpi" style={{ borderTopColor: color }}>
      <Ring pct={pct === undefined ? 100 : pct} color={color} icon={icon} />
      <div className="dash-kpi-body">
        <div className="dash-kpi-label">{label}</div>
        <div className="dash-kpi-value" style={{ color }}>
          <AnimatedNumber value={value} formato={f} />
        </div>
        {sub ? <div className="dash-kpi-sub">{sub}</div> : null}
      </div>
    </div>
  );
}

export function KpiRow({ items }) {
  return (
    <div className="dash-kpis">
      {items.map((k) => (
        <KpiCard key={k.label} {...k} />
      ))}
    </div>
  );
}

// "38.5% del total"
export const pctDe = (parte, total) => (num(total) !== 0 ? (num(parte) / num(total)) * 100 : 0);

// ── Tarjeta con encabezado ──────────────────────────────────────
export function DashCard({ icon, color = "#3b82f6", title, subtitle, actions, children, bodyStyle }) {
  return (
    <div className="dash-card">
      <div className="dash-card-header">
        <div style={{ minWidth: 0 }}>
          <div className="dash-card-title">
            {icon && <i className={icon} style={{ color }} />}
            {title}
          </div>
          {subtitle && <div className="dash-card-subtitle">{subtitle}</div>}
        </div>
        {actions && <div className="dash-card-actions">{actions}</div>}
      </div>
      <div className="dash-card-body" style={bodyStyle}>{children}</div>
    </div>
  );
}

// El div de la grafica SIEMPRE se pinta (amCharts lo busca por id); el aviso va encima
export function ChartBox({ id, height = 300, cargando, vacio }) {
  return (
    <div className="dash-chart" style={{ height }}>
      <div id={id} style={{ width: "100%", height: "100%" }} />
      {cargando ? (
        <div className="dash-chart-overlay">
          <Spinner size="sm" style={{ color: "var(--sidebar-accent)", marginBottom: 8 }} />
          Cargando...
        </div>
      ) : vacio ? (
        <div className="dash-chart-overlay dash-chart-vacio">
          <i className="fas fa-chart-bar" style={{ fontSize: "2rem", opacity: 0.2, marginBottom: 8 }} />
          Sin datos con los filtros seleccionados
        </div>
      ) : null}
    </div>
  );
}

export function EmptyState({ texto = "Sin datos con los filtros seleccionados" }) {
  return (
    <div className="dash-empty">
      <i className="fas fa-chart-bar" />
      {texto}
    </div>
  );
}

// ── Barra horizontal (rankings), igual a la del Inicio ──────────
export function BarRow({ label, value, pct, color, sub }) {
  return (
    <div className="dash-bar">
      <div className="dash-bar-top">
        <span className="dash-bar-label" title={label}>{label}</span>
        <span className="dash-bar-value">{value}</span>
      </div>
      <div className="dash-bar-track">
        <div className="dash-bar-fill" style={{ width: `${Math.max(2, Math.min(num(pct), 100))}%`, background: color }} />
      </div>
      {sub && <div className="dash-bar-sub">{sub}</div>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  amCharts 5
// ════════════════════════════════════════════════════════════════

export function crearRoot(id) {
  const root = am5.Root.new(id);
  root.locale = LOCALE_MX;
  root.setThemes([am5themes_Animated.new(root)]);
  root.interfaceColors.set("text", am5.color("#334155"));
  return root;
}

// Crea varias graficas y regresa la funcion que las desecha (es el return del useLayoutEffect, igual que antes:
// cada render que cambia los datos tira los roots viejos y crea nuevos). Si una grafica falla, las demas siguen
// y la pantalla no se cae; el root que alcanzo a crearse tambien se desecha.
export function montarGraficas(definiciones) {
  const roots = [];
  definiciones.forEach(([id, construir]) => {
    try {
      const root = crearRoot(id);
      roots.push(root);
      construir(root);
    } catch (err) {
      console.error(`Gráfica ${id}:`, err);
    }
  });
  return () => roots.forEach((r) => r.dispose());
}

const colores = () => PALETTE.map((c) => am5.color(c));

// Formato amCharts "positivo|negativo": los negativos salen -$1,234.00 (no $-1,234.00). La "s" final del
// negativo evita que amCharts le vuelva a poner el signo (-$-1,234.00)
const formatoNum = (moneda, base) => (moneda ? "$" + base + "|-$" + base + "s" : base);

const valorTexto = (campo, moneda, sufijo) =>
  "{" + campo + ".formatNumber('" + formatoNum(moneda, "#,##0.00") + "')}" + (sufijo || "");

// Dona con leyenda (a la derecha si cabe, si no abajo) y el total al centro
export function graficaDona(root, { data, valor, categoria, moneda = false, sufijo = "", leyenda = true, centro = "Total" }) {
  const ancho = (root.dom && root.dom.offsetWidth) || 600;
  const alto = (root.dom && root.dom.offsetHeight) || 300;
  const lateral = ancho >= 420;
  const n = data.length;

  const chart = root.container.children.push(
    am5percent.PieChart.new(root, {
      innerRadius: am5.percent(60),
      radius: am5.percent(lateral ? 88 : 82),
      layout: lateral ? root.horizontalLayout : root.verticalLayout,
    })
  );

  const series = chart.series.push(
    am5percent.PieSeries.new(root, {
      valueField: valor,
      categoryField: categoria,
      legendValueText: "{valuePercentTotal.formatNumber('0.0')}%",
      tooltip: am5.Tooltip.new(root, {
        labelText: "[bold]{category}[/]\n" + valorTexto("value", moneda, sufijo) + " · {valuePercentTotal.formatNumber('0.0')}%",
      }),
    })
  );
  series.set("colors", am5.ColorSet.new(root, { colors: colores() }));
  series.labels.template.set("forceHidden", true);
  series.ticks.template.set("forceHidden", true);
  series.slices.template.setAll({ stroke: am5.color("#ffffff"), strokeWidth: 2, cornerRadius: 4 });
  series.slices.template.states.create("hover", { scale: 1.03 });

  if (centro) {
    const total = data.reduce((s, d) => s + num(d[valor]), 0);
    const texto = moneda ? fmtMoneda(total, 0) : fmtNumero(total, 0);
    series.children.push(
      am5.Label.new(root, {
        text: "[fontSize:11px " + GRIS + "]" + centro + "[/]\n[bold fontSize:15px #0f172a]" + texto + "[/]",
        textAlign: "center",
        centerX: am5.p50,
        centerY: am5.p50,
      })
    );
  }

  series.data.setAll(data);

  if (leyenda) {
    // Ancho fijo en pixeles: con porcentaje, el layout horizontal lo reparte contra la dona y la leyenda se recorta
    const anchoLeyenda = Math.round(ancho * 0.46);
    let opciones;
    if (lateral) {
      const cabe = n * 22 < alto - 16;
      opciones = cabe
        ? { width: anchoLeyenda, centerY: am5.p50, y: am5.p50, layout: root.verticalLayout }
        : { width: anchoLeyenda, height: am5.percent(100), layout: root.verticalLayout,
            verticalScrollbar: am5.Scrollbar.new(root, { orientation: "vertical" }) };
    } else {
      const columnas = Math.max(1, Math.floor(ancho / 150));
      const filas = Math.ceil(n / columnas);
      opciones = { centerX: am5.p50, x: am5.p50, layout: root.gridLayout, height: Math.min(filas * 22 + 4, 96),
        verticalScrollbar: am5.Scrollbar.new(root, { orientation: "vertical" }) };
    }
    const legend = chart.children.push(am5.Legend.new(root, opciones));
    // nombre (recortado si es largo) + porcentaje en una columna fija a la derecha
    legend.labels.template.setAll({
      fontSize: 11, fontWeight: "500", fill: am5.color("#334155"),
      oversizedBehavior: "truncate", maxWidth: lateral ? Math.max(70, anchoLeyenda - 100) : 90,
    });
    legend.valueLabels.template.setAll({ fontSize: 11, fill: am5.color(GRIS), width: 44, textAlign: "right" });
    legend.markers.template.setAll({ width: 10, height: 10 });
    legend.markerRectangles.template.setAll({ cornerRadiusTL: 5, cornerRadiusTR: 5, cornerRadiusBL: 5, cornerRadiusBR: 5 });
    legend.itemContainers.template.setAll({ paddingTop: 3, paddingBottom: 3 });
    legend.data.setAll(series.dataItems);
  }

  series.appear(900, 100);
  return { chart, series };
}

// Columnas (o barras horizontales). series: [{ valor, nombre, color }]
export function graficaBarras(root, {
  data, categoria, series: defs, moneda = false, sufijo = "", horizontal = false,
  colorPorBarra = false, min0 = true, etiquetas = false, exportar = false, archivo = "grafica",
}) {
  const ancho = (root.dom && root.dom.offsetWidth) || 800;
  // Ancho de cada columna: si no cabe el nombre derecho, la etiqueta se inclina
  const celda = (ancho - 60) / Math.max(1, data.length);
  const rotar = !horizontal && celda < 90;

  const chart = root.container.children.push(
    am5xy.XYChart.new(root, {
      panX: false, panY: false, wheelX: "none", wheelY: "none",
      paddingLeft: 0, paddingRight: exportar ? 28 : 8,
      layout: root.verticalLayout,
    })
  );

  const catRenderer = horizontal
    ? am5xy.AxisRendererY.new(root, { inversed: true, minGridDistance: 10, cellStartLocation: 0.14, cellEndLocation: 0.86 })
    : am5xy.AxisRendererX.new(root, { minGridDistance: 20, cellStartLocation: 0.12, cellEndLocation: 0.88 });
  catRenderer.labels.template.setAll({
    fontSize: 11, fill: am5.color(GRIS), oversizedBehavior: "truncate",
    maxWidth: horizontal ? Math.min(220, Math.max(110, ancho * 0.2)) : rotar ? 120 : Math.max(50, celda - 8),
  });
  if (rotar) catRenderer.labels.template.setAll({ rotation: -40, centerY: am5.p50, centerX: am5.p100, paddingRight: 4 });
  catRenderer.grid.template.set("visible", false);

  const valRenderer = horizontal ? am5xy.AxisRendererX.new(root, { minGridDistance: 70 }) : am5xy.AxisRendererY.new(root, {});
  valRenderer.labels.template.setAll({ fontSize: 10, fill: am5.color(GRIS) });
  valRenderer.grid.template.setAll({ stroke: am5.color(LINEA), strokeOpacity: 1, strokeWidth: 1 });

  const catAxis = (horizontal ? chart.yAxes : chart.xAxes).push(
    am5xy.CategoryAxis.new(root, { categoryField: categoria, renderer: catRenderer })
  );
  const valSettings = {
    renderer: valRenderer,
    numberFormat: formatoNum(moneda, "#,##0.#a"),
    extraMax: etiquetas ? 0.14 : 0.04,
  };
  if (min0) valSettings.min = 0;
  const valAxis = (horizontal ? chart.xAxes : chart.yAxes).push(am5xy.ValueAxis.new(root, valSettings));

  const paleta = colores();
  defs.forEach((d, i) => {
    const campoValor = horizontal ? "valueX" : "valueY";
    const campoCat = horizontal ? "categoryY" : "categoryX";
    const ajustes = {
      name: d.nombre,
      xAxis: horizontal ? valAxis : catAxis,
      yAxis: horizontal ? catAxis : valAxis,
      tooltip: am5.Tooltip.new(root, {
        labelText: "[bold]{" + campoCat + "}[/]\n" + (d.nombre && defs.length > 1 ? d.nombre + ": " : "") + valorTexto(campoValor, moneda, sufijo),
      }),
    };
    ajustes[campoValor + "Field"] = d.valor;
    ajustes[campoCat + "Field"] = categoria;
    const s = chart.series.push(am5xy.ColumnSeries.new(root, ajustes));

    const color = am5.color(d.color || PALETTE[i % PALETTE.length]);
    s.columns.template.setAll({
      fill: color, stroke: color, strokeOpacity: 0, fillOpacity: 0.9,
      cornerRadiusTL: horizontal ? 0 : 5, cornerRadiusTR: 5, cornerRadiusBR: horizontal ? 5 : 0, cornerRadiusBL: 0,
    });
    s.columns.template.states.create("hover", { fillOpacity: 1 });
    if (colorPorBarra) {
      s.columns.template.adapters.add("fill", (fill, target) => paleta[s.columns.indexOf(target) % paleta.length] || fill);
    }
    if (etiquetas) {
      s.bullets.push(() => {
        const label = am5.Label.new(root, {
          text: "{" + campoValor + ".formatNumber('" + formatoNum(moneda, "#,##0.#a") + "')}",
          fontSize: 10, fill: am5.color(GRIS), populateText: true,
          centerY: horizontal ? am5.p50 : am5.p100, centerX: horizontal ? am5.p0 : am5.p50,
          paddingLeft: 4, paddingRight: 4, paddingBottom: 2,
        });
        // En barras horizontales negativas la etiqueta va a la izquierda de la barra
        if (horizontal) {
          label.adapters.add("centerX", (cx, target) =>
            target.dataItem && target.dataItem.get("valueX") < 0 ? am5.p100 : cx
          );
        }
        return am5.Bullet.new(root, { locationX: horizontal ? 1 : 0.5, locationY: horizontal ? 0.5 : 1, sprite: label });
      });
    }
    s.data.setAll(data);
    s.appear(900);
  });
  catAxis.data.setAll(data);

  if (defs.length > 1) {
    const legend = chart.children.push(am5.Legend.new(root, { centerX: am5.p50, x: am5.p50, marginTop: 6 }));
    legend.labels.template.setAll({ fontSize: 11, fontWeight: "500" });
    legend.markers.template.setAll({ width: 10, height: 10 });
    legend.markerRectangles.template.setAll({ cornerRadiusTL: 5, cornerRadiusTR: 5, cornerRadiusBL: 5, cornerRadiusBR: 5 });
    legend.data.setAll(chart.series.values);
  }

  const cursor = chart.set("cursor", am5xy.XYCursor.new(root, { behavior: "none" }));
  cursor.lineY.set("visible", false);
  if (horizontal) cursor.lineX.set("visible", false);

  // Menu de exportar de amCharts (imagen / impresion), como lo tenian las graficas originales
  if (exportar) {
    am5plugins_exporting.Exporting.new(root, {
      menu: am5plugins_exporting.ExportingMenu.new(root, {}),
      filePrefix: archivo,
    });
  }

  chart.appear(900, 100);
  return { chart };
}
